<script setup lang="ts">
  import {
    ApplicationIntentApplicationType,
    ApplicationIntentStatus,
    JobApplicationSource,
    JobApplicationType,
    JobDetails,
  } from '@borg/types';
  import { MpButton, MpModal, MpText, openMpConfirmModal } from '@borg/ui';

  const props = defineProps<{ source?: JobApplicationSource }>();

  const OFFLINE_APPLICATION_TYPES: JobApplicationType[] = ['phone', 'letter', 'info', 'personal'];
  const { t } = useI18n();
  const { $gtag } = useNuxtApp();
  const userStore = useUserStore();
  const job = jobRepository.use();
  const isOpen = ref(false);
  const sectionsVisibility = reactive({
    phone: false,
    letter: false,
    personal: false,
  });

  const showApplicationButton = computed(
    () => !!job.value?.applicationTypes.length && !job.value?.hideApplicationLink,
  );

  function logIntent(appType: ApplicationIntentApplicationType, status: ApplicationIntentStatus) {
    if (job.value) {
      applicationIntentsService.create({
        jobId: job.value?.id,
        applicationType: appType,
        status: status,
        source: props.source,
      });
    }
  }

  function isSectionButtonVisible(appType: JobDetails['applicationTypes'][number]) {
    if (appType.type === 'phone') {
      return !sectionsVisibility.phone;
    }
    if (appType.type === 'letter') {
      return !sectionsVisibility.letter;
    }
    if (appType.type === 'personal') {
      return !sectionsVisibility.personal;
    }

    return true;
  }

  function invokeFullApplication() {
    isOpen.value = false;
    if (job.value) {
      navigateToJobApplication(job.value.id, job.value.slug, { source: props.source });
    }
  }

  function invokePhoneApplication() {
    sectionsVisibility.phone = true;
    logIntent('phone', 'completed');
  }

  function invokePersonalApplication() {
    sectionsVisibility.personal = true;
    logIntent('personal', 'completed');
  }

  function invokeLetterApplication() {
    sectionsVisibility.letter = true;
    logIntent('letter', 'completed');
  }

  function invokeExternalApplication(applicationType: JobApplicationType) {
    isOpen.value = false;

    if (job.value) {
      navigateToExternalJobApplication(job.value.id, job.value.slug, userStore.isAuthenticated, {
        source: props.source,
        applicationType,
      });
    }
  }

  function getApplicationTextAndIcon(appType: JobDetails['applicationTypes'][number]['type']): {
    title: string;
    label: string;
  } {
    switch (appType) {
      case 'full':
        return {
          title: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_PORTAL_LABEL'),
          label: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_BUTTON_LABEL'),
        };
      case 'quick':
        return {
          title: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_QUICK_APPLY_LABEL'),
          label: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_BUTTON_LABEL'),
        };
      case 'phone':
        return {
          title: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_PHONE_LABEL'),
          label: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_PHONE_BUTTON_LABEL'),
        };
      case 'info':
        return {
          title: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_PERSONAL_LABEL'),
          label: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_BUTTON_LABEL'),
        };
      case 'letter':
        return {
          title: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_ADDRESS_LABEL'),
          label: t(
            'JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_ADDRESS_BUTTON_LABEL',
          ),
        };
      case 'personal':
        return {
          title: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_PERSONAL_LABEL'),
          label: t(
            'JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_ADDRESS_BUTTON_LABEL',
          ),
        };
      case 'external':
      case 'direct_link':
        return {
          title: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_VIA_EXTERNAL_LABEL'),
          label: t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.SIGN_IN_BUTTON_LABEL'),
        };
    }
  }

  function onMakeApplication(appType: JobDetails['applicationTypes'][number]) {
    switch (appType.type) {
      case 'full':
      case 'quick':
        invokeFullApplication();
        break;
      case 'phone':
        $gtag?.logJob('job-apply:offline');
        invokePhoneApplication();
        break;
      case 'letter':
        $gtag?.logJob('job-apply:offline');
        invokeLetterApplication();
        break;
      case 'info':
      case 'personal':
        $gtag?.logJob('job-apply:offline');
        invokePersonalApplication();
        break;
      case 'external':
      case 'direct_link':
        invokeExternalApplication(appType.type);
        break;
    }
  }

  async function onContinue() {
    if (job.value?.userInfo.appliedAt) {
      const confirmed = await openMpConfirmModal({
        text: t('JOB_DETAILS_PAGE.APPLY_AGAIN_CONFIRM_MODAL.TEXT'),
        confirmButtonLabel: t('JOB_DETAILS_PAGE.APPLY_AGAIN_CONFIRM_MODAL.CONFIRM_BUTTON_LABEL'),
        cancelButtonLabel: t('JOB_DETAILS_PAGE.APPLY_AGAIN_CONFIRM_MODAL.CANCEL_BUTTON_LABEL'),
      });
      if (!confirmed) {
        return;
      }
    }

    isOpen.value = true;

    if (job.value?.applicationTypes.length === 1) {
      const applicationType = job.value?.applicationTypes[0];
      if (!OFFLINE_APPLICATION_TYPES.includes(applicationType.type)) {
        onMakeApplication(applicationType);
      }
    }
  }

  function onClose() {
    isOpen.value = false;
    sectionsVisibility.phone = false;
    sectionsVisibility.letter = false;
    sectionsVisibility.personal = false;
  }
</script>

<template>
  <slot :on-continue="onContinue">
    <MpButton
      v-if="job?.userInfo.appliedAt"
      :data-test="`apply-again-button-${source}`"
      class="apply-button"
      :text="$t('JOB_DETAILS_PAGE.APPLIED_BUTTON_LABEL')"
      variant="secondary"
      right-icon="check_mark"
      @click="onContinue"
    />
    <MpButton
      v-else-if="showApplicationButton"
      :data-test="`apply-button-${source}`"
      class="apply-button"
      :text="$t('JOB_DETAILS_PAGE.APPLY_BUTTON_LABEL')"
      @click="onContinue"
    />
  </slot>
  <MpModal
    :title="$t('JOB_DETAILS_PAGE.CHOOSE_APPLICATION_TYPE_MODAL.TITLE')"
    :is-open="isOpen"
    size="sm"
    test="choose-application-type-modal"
    @overlay-click="isOpen = false"
    @update:is-open="onClose"
  >
    <div
      v-for="appType in job?.applicationTypes ?? []"
      :key="appType.type"
      class="app-type"
    >
      <MpText as="h6">
        {{ getApplicationTextAndIcon(appType.type).title }}
      </MpText>
      <MpButton
        v-if="isSectionButtonVisible(appType)"
        :data-test="`${appType.type}-apply-button`"
        :text="getApplicationTextAndIcon(appType.type).label"
        is-full-width
        @click="onMakeApplication(appType)"
      />
      <div
        v-if="appType.type === 'phone' && sectionsVisibility.phone"
        class="app-type__phones-list"
        :data-test="`application-${appType.type}`"
      >
        <MpText
          v-if="appType.data?.mainPhone"
          as="a"
          link
          :href="`tel:${appType.data?.mainPhone}`"
          style="margin: 2px"
        >
          {{ appType.data?.mainPhone }}
        </MpText>
        <MpText
          v-if="appType.data?.additionalPhone"
          as="a"
          link
          :href="`tel:${appType.data?.additionalPhone}`"
          style="margin: 2px"
        >
          {{ appType.data?.additionalPhone }}
        </MpText>
      </div>
      <div
        v-if="appType.type === 'letter' && sectionsVisibility.letter"
        :data-test="`application-${appType.type}`"
      >
        <MpText>{{ appType.data?.address }}</MpText>
        <br />
        <MpText>{{ appType.data?.zip }} {{ appType.data?.city }}</MpText>
      </div>
      <div
        v-if="appType.type === 'personal' && sectionsVisibility.personal"
        :data-test="`application-${appType.type}`"
      >
        <MpText>{{ appType.data?.address }}</MpText>
        <br />
        <MpText>{{ appType.data?.zip }} {{ appType.data?.city }}</MpText>
      </div>
    </div>
  </MpModal>
</template>

<style scoped lang="scss">
  .app-type {
    margin-bottom: var(--mp-space-70);

    &__phones-list {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-40);
      width: fit-content;
    }

    &:first-child {
      margin-top: var(--mp-space-70);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .apply-button {
    flex: 1;
    @include screen-md {
      flex: unset;
    }
  }
</style>
